import React, { useEffect, useRef, useState } from 'react';
import {
  loadConnectAndInitialize,
  StripeConnectInstance,
  AppearanceVariables,
} from '@stripe/connect-js';
import {
  ConnectComponentsProvider,
  ConnectAccountOnboarding,
} from '@stripe/react-connect-js';

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!;
const functionsBaseUrl = process.env.REACT_APP_FUNCTION_BASE_URL!;
const publicUrl = process.env.PUBLIC_URL || '';

const searchParams = new URLSearchParams(window.location.search);

const themeParam = searchParams.get('theme');
const theme = themeParam === 'dark' ? 'dark' : 'light';

type ThemeName = 'light' | 'dark';

const palettes = {
  light: {
    brand: '#09C4D8',
    brandSoft: '#DCFBFF',
    brandStrong: '#063647',
    page: '#F4F8FA',
    surface: '#FFFFFF',
    surfaceSoft: '#EDF7F9',
    surfaceStrong: '#0B2633',
    ink: '#0C1720',
    text: '#172430',
    muted: '#61717D',
    border: 'rgba(12, 23, 32, 0.12)',
    borderStrong: 'rgba(12, 23, 32, 0.22)',
    danger: '#B3261E',
    dangerSoft: 'rgba(179, 38, 30, 0.10)',
    dangerBorder: 'rgba(179, 38, 30, 0.28)',
    successSoft: 'rgba(183, 243, 109, 0.24)',
  },
  dark: {
    brand: '#12D5E9',
    brandSoft: 'rgba(18, 213, 233, 0.10)',
    brandStrong: '#B8F8FF',
    page: '#08151B',
    surface: '#10212A',
    surfaceSoft: '#142B35',
    surfaceStrong: '#071116',
    ink: '#F5FBFC',
    text: '#E9F3F6',
    muted: '#A3B8C1',
    border: 'rgba(221, 246, 250, 0.13)',
    borderStrong: 'rgba(221, 246, 250, 0.22)',
    danger: '#FFB4AB',
    dangerSoft: 'rgba(255, 180, 171, 0.10)',
    dangerBorder: 'rgba(255, 180, 171, 0.28)',
    successSoft: 'rgba(205, 251, 131, 0.14)',
  },
} as const;

function getStripeAppearance(themeName: ThemeName): AppearanceVariables {
  const palette = palettes[themeName];

  return {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontSizeBase: '16px',
    spacingUnit: '8px',
    borderRadius: '8px',
    colorPrimary: palette.brand,
    colorBackground: palette.surface,
    colorText: palette.text,
    colorSecondaryText: palette.muted,
    colorDanger: palette.danger,
    colorBorder: palette.borderStrong,
    offsetBackgroundColor: palette.surfaceSoft,
    formBackgroundColor: palette.surface,
    formHighlightColorBorder: palette.brand,
    formAccentColor: palette.brand,
    buttonBorderRadius: '8px',
    buttonPrimaryColorBackground: palette.brand,
    buttonPrimaryColorBorder: palette.brand,
    buttonPrimaryColorText: '#061015',
    buttonSecondaryColorBackground: palette.surface,
    buttonSecondaryColorBorder: palette.borderStrong,
    buttonSecondaryColorText: palette.ink,
    actionPrimaryColorText: palette.brandStrong,
    actionPrimaryTextDecorationColor: palette.brand,
    actionSecondaryColorText: palette.muted,
    badgeNeutralColorBackground: palette.surfaceSoft,
    badgeNeutralColorText: palette.muted,
    badgeNeutralColorBorder: palette.border,
    badgeSuccessColorBackground: palette.successSoft,
    badgeSuccessColorText: palette.brandStrong,
    badgeSuccessColorBorder: palette.border,
    badgeWarningColorBackground: palette.brandSoft,
    badgeWarningColorText: palette.brandStrong,
    badgeWarningColorBorder: palette.border,
    badgeDangerColorBackground: palette.dangerSoft,
    badgeDangerColorText: palette.danger,
    badgeDangerColorBorder: palette.dangerBorder,
    formBorderRadius: '8px',
    badgeBorderRadius: '999px',
    overlayBorderRadius: '8px',
    overlayBackdropColor:
      themeName === 'dark'
        ? 'rgba(7, 17, 22, 0.72)'
        : 'rgba(7, 27, 37, 0.34)',
    bodyMdFontSize: '15px',
    bodyMdFontWeight: '400',
    bodySmFontSize: '13px',
    bodySmFontWeight: '500',
    headingXlFontSize: '27px',
    headingXlFontWeight: '700',
    headingLgFontSize: '24px',
    headingLgFontWeight: '700',
    headingMdFontSize: '20px',
    headingMdFontWeight: '700',
    headingSmFontSize: '17px',
    headingSmFontWeight: '700',
    headingXsFontSize: '15px',
    headingXsFontWeight: '700',
    labelMdFontSize: '14px',
    labelMdFontWeight: '700',
    labelSmFontSize: '12px',
    labelSmFontWeight: '700',
  };
}

async function initStripeConnect(
  clientSecret: string,
  themeName: ThemeName
): Promise<StripeConnectInstance> {
  if (!publishableKey) {
    throw new Error('Missing REACT_APP_STRIPE_PUBLISHABLE_KEY');
  }
  if (!functionsBaseUrl) {
    throw new Error('Missing REACT_APP_FUNCTION_BASE_URL');
  }

  return loadConnectAndInitialize({
    publishableKey,
    fetchClientSecret: async () => clientSecret,
    appearance: {
      variables: getStripeAppearance(themeName),
    },
    fonts: [
      {
        cssSrc:
          'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
      },
    ],
  });
}

// Notify that onboarding is done
function notifyHostOnboardingComplete() {
  const payload = { type: 'STRIPE_ONBOARDING_COMPLETE' };
  const message = JSON.stringify(payload);

  const returnUrl = searchParams.get('returnUrl');

  // 1) React Native WebView (mobile - not used atm)
  if ((window as any).ReactNativeWebView?.postMessage) {
    (window as any).ReactNativeWebView.postMessage(message);
  }

  // 2) Parent window (when this app is in an <iframe> on web)
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, '*');
  }

  // 3) Standalone mobile browser opened from the app:
  //    try to bounce back via deep link
  const isMobile =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  if (isMobile && returnUrl) {
    window.location.href = returnUrl;
  }
}

function StatusMessage({
  title,
  body,
  loading = false,
  tone = 'default',
}: {
  title: string;
  body?: string;
  loading?: boolean;
  tone?: 'default' | 'error';
}) {
  return (
    <div className="status-page">
      <div
        className={`status-card ${
          tone === 'error' ? 'status-card--error' : ''
        }`}
      >
        {loading ? <span className="loader" aria-hidden="true" /> : null}
        <h1>{title}</h1>
        {body ? <p>{body}</p> : null}
      </div>
    </div>
  );
}

const App: React.FC = () => {
  const [connectInstance, setConnectInstance] =
    useState<StripeConnectInstance | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  const token = searchParams.get('token');

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, []);

  // 1) First, exchange the onboarding token for a client secret
  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${functionsBaseUrl}/stripeOnboardingTokenExchangeForSession`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || 'Failed to create account session');
        }

        if (!cancelled) {
          setClientSecret(data.client_secret as string);
        }
      } catch (err: any) {
        console.error(err);
        if (!cancelled) {
          setError(err.message || 'Failed to create account session');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // 2) Once we have a client secret, initialise Stripe Connect
  useEffect(() => {
    // If there is no token, do NOT try to initialise Stripe
    if (!token) return;
    if (!clientSecret) return;

    if (hasInitialized.current) return;
    hasInitialized.current = true;

    (async () => {
      try {
        const instance = await initStripeConnect(clientSecret, theme);
        setConnectInstance(instance);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to initialise Stripe Connect');
      }
    })();
  }, [clientSecret, token]);

  if (!token) {
    return (
      <StatusMessage
        title="Missing token in URL."
        body="Please go back and try again. If the problem persists, please contact support."
      />
    );
  }

  if (error) {
    return (
      <StatusMessage
        title={`Error: ${error}`}
        body="Please go back and try again. If the problem persists, please contact support."
        tone="error"
      />
    );
  }

  if (!clientSecret) {
    return <StatusMessage title="Preparing payout setup..." loading />;
  }

  if (!connectInstance) {
    return <StatusMessage title="Loading payout setup..." loading />;
  }

  return (
    <ConnectComponentsProvider connectInstance={connectInstance}>
      <div className="onboarding-page">
        <section className="onboarding-panel" aria-labelledby="payout-title">
          <div className="onboarding-panel__wash" aria-hidden="true" />
          <div className="onboarding-panel__content">
            <header className="onboarding-header">
              <div className="brand-lockup">
                <span className="brand-badge" aria-hidden="true">
                  <img
                    src={`${publicUrl}/away-tours-icon.png`}
                    alt=""
                    className="brand-logo"
                  />
                </span>
                <div>
                  <p className="eyebrow">Stripe Connect</p>
                  <h1 id="payout-title">AwayTours - Set Up Payouts</h1>
                </div>
              </div>

              <p className="lede">
                We have partnered with Stripe to provide safe and reliable
                payments in AwayTours.
              </p>

              <p className="supporting-copy">
                Complete these details so users can pay you for trips you
                organise.
              </p>
            </header>

            <div className="stripe-card">
              <ConnectAccountOnboarding
                onExit={() => {
                  console.log('Onboarding exited');
                  // When Stripe's embedded onboarding closes, notify the host so the app can navigate away.
                  notifyHostOnboardingComplete();
                }}
              />
            </div>
          </div>
        </section>
      </div>
    </ConnectComponentsProvider>
  );
};

export default App;
